import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import "./WelcomePage.css";
import blockClubLogo from "../../media/blockclub-logo.png";
import LoadingPage from "../Loading/LoadingPage";

const withCacheBuster = (url, key = Date.now()) =>
  url ? `${url}${url.includes("?") ? "&" : "?"}v=${key}` : url;

function QRSignupCard() {
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get("company_id");
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

  // Branding
  const [companyName, setCompanyName] = useState("");
  const [logoSrc, setLogoSrc] = useState(null);
  const [logoLoaded, setLogoLoaded] = useState(false);

  // Public welcome page data
  const [welcome, setWelcome] = useState(null);
  const [isLoadingWelcome, setIsLoadingWelcome] = useState(true);

  // Form state
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Treasure box animation state (mobile only) - COMMENTED OUT FOR REVISION
  // const [treasureBoxOpened, setTreasureBoxOpened] = useState(false);
  // const [boxAnimationStage, setBoxAnimationStage] = useState("closed"); // 'closed', 'opening', 'scroll-emerging', 'scroll-unfolding', 'complete'
  const [isMobile, setIsMobile] = useState(false);
  // const [pageVisible, setPageVisible] = useState(false);

  // Gallery carousel state
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const trackRef = React.useRef(null);
  const isSnappingRef = React.useRef(false);       
  const isAnimatingRef = React.useRef(false);
  // --- swipe / drag support ---
  const dragStartX = React.useRef(0);
  const dragDeltaX = React.useRef(0);
  const isDraggingRef = React.useRef(false);
  const SWIPE_THRESHOLD = 40;

  // Ignore swipe logic when starting from controls (dots, buttons, links, inputs)
  const isFromControls = (e) => {
    const t = e.target;
    return t?.closest?.('.qr-gallery-indicators')
        || t?.closest?.('button,a,input,textarea,select,label');
  };

  // Pointer helpers (works for touch, pen, mouse)
  const handlePointerDown = (e) => handleDragStart(e);
  const handlePointerMove = (e) => handleDragMove(e);
  const handlePointerUp = (e) => handleDragEnd(e);

  const getClientX = (e) =>
    'touches' in e ? e.touches[0].clientX : ('changedTouches' in e ? e.changedTouches[0].clientX : e.clientX);

  const handleDragStart = (e) => {
    pauseAutoThenResume();

    if (isFromControls(e)) return; 
    isDraggingRef.current = true;
    dragStartX.current = getClientX(e);
    dragDeltaX.current = 0;
    setIsTransitioning(false);
  };

  const handleDragMove = (e) => {
    if (!isDraggingRef.current || isFromControls(e)) return;
    const x = getClientX(e);
    dragDeltaX.current = x - dragStartX.current;
    // prevent the page from vertically scrolling when a horizontal swipe is happening
    if (Math.abs(dragDeltaX.current) > 8) {
      e.preventDefault?.();
      e.stopPropagation?.();
    }
  };

  const handleDragEnd = (e) => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsTransitioning(true);
    const dx = dragDeltaX.current || (getClientX(e) - dragStartX.current);
    if (Math.abs(dx) < SWIPE_THRESHOLD) return; // not far enough
    if (dx < 0) {
      // next
      isAnimatingRef.current = true;
      setCurrentPhotoIndex((prev) => prev + 1);
    } else {
      isAnimatingRef.current = true;
      setCurrentPhotoIndex((prev) => prev - 1);
    }
  };

  // auto-advance control
  const autoTimerRef = React.useRef(null);
  const autoResumeRef = React.useRef(null);

  const startAuto = React.useCallback(() => {
    if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    autoTimerRef.current = setInterval(() => {
      if (isSnappingRef.current || isAnimatingRef.current) return;
      isAnimatingRef.current = true;
      setCurrentPhotoIndex((prev) => prev + 1);
    }, 5000);
  }, []);

  const stopAuto = React.useCallback(() => {
    if (autoTimerRef.current) {
      clearInterval(autoTimerRef.current);
      autoTimerRef.current = null;
    }
  }, []);

  const pauseAutoThenResume = React.useCallback((delay = 4000) => {
    // stop auto now
    stopAuto();
    // debounce resume
    if (autoResumeRef.current) clearTimeout(autoResumeRef.current);
    autoResumeRef.current = setTimeout(() => {
      startAuto();
    }, delay);
  }, [startAuto, stopAuto]);

  // --- wheel gesture control ---
  const wheelAccumRef = React.useRef(0);
  const wheelLockRef = React.useRef(false);
  const wheelIdleTimerRef = React.useRef(null);

  // new helpers to robustly unlock after one slide
  const lockUntilRef = React.useRef(0);       // time-based guard
  const tinyDxCountRef = React.useRef(0);     // inertia “near zero” counter
  const lastSlideTsRef = React.useRef(0);

  // tweakables
  const WHEEL_THRESHOLD_PX = 60;      // distance to commit a slide
  const LOCK_MIN_MS = 220;            // minimum lock duration after committing a slide
  const UNLOCK_TINY_DX = 1.5;         // consider inertia “idle” when |dx| < this
  const UNLOCK_TINY_EVENTS = 2;       // need this many tiny dx events to unlock
  const WHEEL_IDLE_UNLOCK_MS = 600;   // hard fallback unlock if user stops for a bit

  const handleWheel = (e) => {
    // keep auto-advance, just pause briefly after interaction
    pauseAutoThenResume(3000);

    // Only react to primarily horizontal two-finger scrolls
    const absX = Math.abs(e.deltaX);
    const absY = Math.abs(e.deltaY);
    if (absX <= absY) return;

    e.preventDefault?.();
    e.stopPropagation?.();

    const now = performance.now();

    // If we're locked, wait until inertia settles OR a short time passes
    if (wheelLockRef.current) {
      // soft unlock when wheel movement becomes tiny for a couple frames
      if (now >= lockUntilRef.current && Math.abs(e.deltaX) < UNLOCK_TINY_DX) {
        tinyDxCountRef.current += 1;
        if (tinyDxCountRef.current >= UNLOCK_TINY_EVENTS) {
          wheelLockRef.current = false;
          wheelAccumRef.current = 0;
          tinyDxCountRef.current = 0;
        }
      } else if (Math.abs(e.deltaX) >= UNLOCK_TINY_DX) {
        // still moving with momentum – keep waiting and reset tiny counter
        tinyDxCountRef.current = 0;
      }

      // hard fallback unlock after idle
      clearTimeout(wheelIdleTimerRef.current);
      wheelIdleTimerRef.current = setTimeout(() => {
        wheelLockRef.current = false;
        wheelAccumRef.current = 0;
        tinyDxCountRef.current = 0;
      }, WHEEL_IDLE_UNLOCK_MS);
      return;
    }

    // accumulate horizontal distance for this gesture
    wheelAccumRef.current += e.deltaX;

    // crossed threshold? commit EXACTLY one slide and lock
    if (Math.abs(wheelAccumRef.current) >= WHEEL_THRESHOLD_PX) {
      const dx = wheelAccumRef.current;

      if (dx > 0) {
        isAnimatingRef.current = true;
        setCurrentPhotoIndex((prev) => prev + 1);
      } else {
        isAnimatingRef.current = true;
        setCurrentPhotoIndex((prev) => prev - 1);
      }

      // lock so this same gesture can't trigger more moves
      wheelLockRef.current = true;
      wheelAccumRef.current = 0;
      tinyDxCountRef.current = 0;
      lastSlideTsRef.current = now;
      setIsTransitioning(true);

      // keep the lock at least a short time; then allow “tiny-dx” unlock
      lockUntilRef.current = now + LOCK_MIN_MS;

      // also set a fallback unlock if the user stops
      clearTimeout(wheelIdleTimerRef.current);
      wheelIdleTimerRef.current = setTimeout(() => {
        wheelLockRef.current = false;
        wheelAccumRef.current = 0;
        tinyDxCountRef.current = 0;
      }, WHEEL_IDLE_UNLOCK_MS);

      return;
    }

    // not over threshold yet → gentle snap back after brief pause
    clearTimeout(wheelIdleTimerRef.current);
    wheelIdleTimerRef.current = setTimeout(() => {
      wheelAccumRef.current = 0;
      tinyDxCountRef.current = 0;
      setIsTransitioning(true);
    }, 120);
  };

  /* -------- Check if mobile -------- */
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 820);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  /* -------- ANIMATION LOGIC COMMENTED OUT FOR REVISION -------- */
  // /* -------- Ensure page is always visible after animation completes -------- */
  // useEffect(() => {
  //   if (isMobile && treasureBoxOpened) {
  //     // Ensure page becomes visible shortly after animation starts
  //     const visibilityTimer = setTimeout(() => {
  //       setPageVisible(true);
  //     }, 100);

  //     // Fallback: force page visible if animation takes too long
  //     const fallbackTimer = setTimeout(() => {
  //       setPageVisible(true);
  //     }, 2000);

  //     return () => {
  //       clearTimeout(visibilityTimer);
  //       clearTimeout(fallbackTimer);
  //     };
  //   } else if (!isMobile) {
  //     // On desktop, page is always visible
  //     setPageVisible(true);
  //   }
  // }, [isMobile, treasureBoxOpened]);

  /* -------- Auto-advance carousel -------- */
  useEffect(() => {
    if (!welcome?.photos || welcome.photos.length === 0) return;
    startAuto();
    return () => {
      stopAuto();
      if (autoResumeRef.current) clearTimeout(autoResumeRef.current);
    };
  }, [welcome?.photos, startAuto, stopAuto]);

  // ===== per-view helpers (desktop shows 3, mobile shows 1) =====
  const total = welcome?.photos?.length ?? 0;
  const basePerView = isMobile ? 1 : 3;
  const perView = Math.min(basePerView, Math.max(1, total)); // never > total
  const slideWidthPct = 100 / perView;

  useEffect(() => {
    if (!total) return;
    // Snap (no animation) to the correct starting offset = perView
    setIsTransitioning(false);
    setCurrentPhotoIndex(perView);
    requestAnimationFrame(() => setIsTransitioning(true));
  }, [total, perView]);

  /* -------- Handle seamless loop reset AFTER the slide animation (guarded) -------- */
  useEffect(() => {
    const el = trackRef.current;
    const total = welcome?.photos?.length ?? 0;
    if (!el || !total) return;

    const onTransitionEnd = (e) => {
      if (e.target !== el || e.propertyName !== 'transform') return;
      if (isSnappingRef.current) return;

      // Wrap when we land inside the leading or trailing ghost zone
      if (currentPhotoIndex < perView) {
        isSnappingRef.current = true;
        setIsTransitioning(false);
        setCurrentPhotoIndex(currentPhotoIndex + total);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsTransitioning(true);
            isSnappingRef.current = false;
            isAnimatingRef.current = false;
          });
        });
      } else if (currentPhotoIndex >= total + perView) {
        isSnappingRef.current = true;
        setIsTransitioning(false);
        setCurrentPhotoIndex(currentPhotoIndex - total);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsTransitioning(true);
            isSnappingRef.current = false;
            isAnimatingRef.current = false;
          });
        });
      } else {
        isAnimatingRef.current = false;
      }
    };

    el.addEventListener('transitionend', onTransitionEnd);
    return () => el.removeEventListener('transitionend', onTransitionEnd);
  }, [currentPhotoIndex, welcome?.photos, perView]);

  /* -------- Manual indicator click -------- */
  const handleIndicatorClick = (index) => {
    pauseAutoThenResume();
    setIsTransitioning(true);
    isAnimatingRef.current = true;
    setCurrentPhotoIndex(index + 1);
  };

  /* -------- Branding (logo + name) -------- */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!companyId) {
          setLogoSrc(blockClubLogo);
          return;
        }
        const res = await fetch(
          `${apiUrl}/loyalty/branding/companies/${companyId}`,
          { method: "GET", headers: { "Content-Type": "application/json" } }
        );
        if (!res.ok) throw new Error(`Branding status ${res.status}`);
        const data = await res.json();
        if (!mounted) return;

        setCompanyName(data.name || "");
        const rawUrl = (data.logoUrl || "").trim();
        const busted = rawUrl ? withCacheBuster(rawUrl) : "";
        setLogoLoaded(false);
        setLogoSrc(busted || blockClubLogo);
      } catch {
        if (mounted) {
          setLogoLoaded(false);
          setLogoSrc(blockClubLogo);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [companyId, apiUrl]);

  useEffect(() => {
    if (!logoSrc) return;
    setLogoLoaded(false);
    const img = new Image();
    img.onload = () => setLogoLoaded(true);
    img.onerror = () => setLogoLoaded(true);
    img.src = logoSrc;
  }, [logoSrc]);

  /* -------- Public welcome page -------- */
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!companyId) {
        setIsLoadingWelcome(false);
        return;
      }
      setIsLoadingWelcome(true);
      try {
        const res = await fetch(
          `${apiUrl}/welcome/public-pages?company_id=${companyId}`,
          { method: "GET", headers: { "Content-Type": "application/json" } }
        );
        if (res.status === 204) {
          if (mounted) {
            setWelcome(null);
            setIsLoadingWelcome(false);
          }
          return;
        }
        if (!res.ok) throw new Error(`Welcome status ${res.status}`);
        const data = await res.json();
        if (!mounted) return;
        setWelcome({
          program_name: data.program_name || "",
          store_description: data.store_description || "",
          benefits: data.benefits || "",
          photos: Array.isArray(data.photos) ? data.photos : [],
        });
      } catch {
        if (mounted) setWelcome(null);
      } finally {
        if (mounted) setIsLoadingWelcome(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [companyId, apiUrl]);

  // Helpers
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim())
      return setErrorMessage("Please enter your email address.");
    if (!validateEmail(email))
      return setErrorMessage("Please enter a valid email address.");
    if (!consent)
      return setErrorMessage(
        "Please agree to receive promotional messages to continue."
      );
    if (!companyId)
      return setErrorMessage("Invalid QR code. Missing company information.");

    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const resp = await fetch(
        `${apiUrl}/loyalty/signup?company_id=${companyId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );
      if (!resp.ok) {
        let detail = `Server error (${resp.status})`;
        try {
          const err = await resp.json();
          detail = err.detail || detail;
        } catch {}
        throw new Error(detail);
      }
      const data = await resp.json();
      const displayName =
        welcome?.program_name || companyName || data.company_name || "us";
      setSuccessMessage(
        `Thanks for joining! You'll receive a welcome email from ${displayName} with exclusive offers soon.`
      );
      setEmail("");
      setConsent(false);
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Prefer program name over company name
  const heading =
    welcome?.program_name ||
    (companyName && companyName.toLowerCase() !== "name" ? companyName : "") ||
    "Loyalty Program";

  const cleanCompanyName =
    companyName && companyName.toLowerCase() !== "name"
      ? companyName
      : welcome?.program_name || "";

  const overview =
    welcome?.store_description ||
    `At ${
      cleanCompanyName || "our restaurant"
    }, every table feels like a celebration. Join for seasonal offers and member-only perks.`;

  const benefits =
    welcome?.benefits ||
    "Members receive gentle reminders for off-peak specials, surprise perks, and early access to events.";

  /* -------- TREASURE BOX ANIMATION HANDLER COMMENTED OUT FOR REVISION -------- */
  // const handleTreasureBoxClick = () => {
  //   if (boxAnimationStage === "closed") {
  //     setBoxAnimationStage("opening");

  //     // Fallback timeout - if animation doesn't complete in 6 seconds, force it to finish
  //     const fallbackTimeout = setTimeout(() => {
  //       console.warn("Animation timeout - forcing completion");
  //       setBoxAnimationStage("complete");
  //       setTreasureBoxOpened(true);
  //     }, 6000);

  //     // After lid opens (1s), show scroll emerging
  //     const timeout1 = setTimeout(() => {
  //       setBoxAnimationStage("scroll-emerging");
  //     }, 1000);

  //     // After scroll emerges (1.5s total), start unfolding
  //     const timeout2 = setTimeout(() => {
  //       setBoxAnimationStage("scroll-unfolding");
  //     }, 1500);

  //     // After scroll unfolds (4s total), mark as complete
  //     const timeout3 = setTimeout(() => {
  //       clearTimeout(fallbackTimeout); // Clear fallback if we get here successfully
  //       setBoxAnimationStage("complete");
  //       setTreasureBoxOpened(true);
  //     }, 4000);

  //     // Store timeout IDs for cleanup
  //     return () => {
  //       clearTimeout(fallbackTimeout);
  //       clearTimeout(timeout1);
  //       clearTimeout(timeout2);
  //       clearTimeout(timeout3);
  //     };
  //   }
  // };

  // ===== Hide treasure overlay on mobile =====
  // Show treasure box overlay only on mobile and when not opened yet
  /*
  if (isMobile && !treasureBoxOpened) {
    return (
      <div
        className={`treasure-overlay ${
          boxAnimationStage !== "closed" ? "animating" : ""
        }`}
        onClick={handleTreasureBoxClick}
      >
        {boxAnimationStage === "closed" && (
          <div className="treasure-instruction">
            Tap to unlock your treasure
          </div>
        )}
        <div className={`treasure-box ${boxAnimationStage}`}>
          <div
            className={`treasure-box-lid ${
              boxAnimationStage === "opening" ||
              boxAnimationStage === "scroll-emerging" ||
              boxAnimationStage === "scroll-unfolding"
                ? "opening"
                : ""
            }`}
          >
            <div className="lid-top"></div>
            <div className="lid-detail"></div>
          </div>
          <div className="treasure-box-body">
            <div className="box-front">
              <div className="lock-detail"></div>
              <div className="hinge hinge-left"></div>
              <div className="hinge hinge-right"></div>
            </div>
            <div className="box-side"></div>
          </div>

          {(boxAnimationStage === "scroll-emerging" ||
            boxAnimationStage === "scroll-unfolding") && (
            <div
              className={`golden-treasure ${
                boxAnimationStage === "scroll-unfolding" ? "expanding" : ""
              }`}
            >
              <div className="treasure-glow"></div>
              <div className="golden-coin">
                <div className="coin-face coin-front">
                  <div className="coin-outer-ring"></div>
                  <div className="coin-inner-circle">
                    {logoSrc && (
                      <img src={logoSrc} alt="Logo" className="coin-logo" />
                    )}
                  </div>
                  <div className="coin-shine"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  */

  // Show loading component while page is not visible on mobile after treasure box opens
  /*
  if (isMobile && treasureBoxOpened && !pageVisible) {
    return <LoadingPage />;
  }
  */

  return (
    <div
      className="qr-welcome-page" // Animation class removed for revision
      role="main"
    >
      {/* HERO */}
      <header className="qr-hero">
        <div className="qr-hero-inner">
          <div className="qr-logo-wrap">
            {!logoLoaded && <div className="qr-logo-skel" />}
            {logoSrc && (
              <img
                src={logoSrc}
                alt={heading ? `${heading} Logo` : "Logo"}
                className={`qr-logo ${logoLoaded ? "is-loaded" : ""}`}
                onLoad={() => setLogoLoaded(true)}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = blockClubLogo;
                  setLogoLoaded(true);
                }}
                loading="eager"
                fetchpriority="high"
                decoding="async"
              />
            )}
          </div>
          <h1 className="qr-title">{heading}</h1>
          {/* <p className="qr-subtitle">
            Join our membership for savings and treats.
          </p> */}
        </div>
      </header>

      {/* GALLERY */}
      {(welcome?.photos?.length ?? 0) > 0 && (
        <section className="qr-gallery-wrap" aria-label="Gallery">
          <div className="qr-gallery-inner">
            {isLoadingWelcome ? (
              <LoadingPage />
            ) : (
              <div
                className="qr-gallery-carousel"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onWheel={handleWheel}   
              >
                <div
                  className="qr-gallery-track"
                  ref={trackRef}
                  style={{
                    transform: `translateX(-${currentPhotoIndex * slideWidthPct}%)`,
                    transition: isTransitioning ? "transform 0.5s ease-in-out" : "none",
                  }}
                >
                  {/* Leading ghosts: last 'perView' slides */}
                  {total > 0 &&
                    Array.from({ length: perView }).map((_, i) => {
                      const idx = (total - perView + i + total) % total;
                      return (
                        <div key={`ghost-head-${i}`} className="qr-gallery-item">
                          <img
                            draggable={false}
                            src={welcome.photos[idx]}
                            alt="ghost head"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                          />
                        </div>
                      );
                    })}

                  {/* Real slides */}
                  {welcome.photos.map((photo, i) => (
                    <div key={i} className="qr-gallery-item">
                      <img
                        draggable={false}
                        src={photo}
                        alt={`Gallery ${i + 1}`}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    </div>
                  ))}

                  {/* Trailing ghosts: first 'perView' slides */}
                  {total > 0 &&
                    Array.from({ length: perView }).map((_, i) => {
                      const idx = i % total;
                      return (
                        <div key={`ghost-tail-${i}`} className="qr-gallery-item">
                          <img
                            draggable={false}
                            src={welcome.photos[idx]}
                            alt="ghost tail"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                          />
                        </div>
                      );
                    })}
                </div>
                {/* Carousel indicators */}
                <div className="qr-gallery-indicators">
                  {welcome.photos.map((_, i) => (
                    <button
                      key={i}
                      className={`qr-gallery-indicator ${
                        i === ((currentPhotoIndex - perView + total) % total) ? "active" : ""
                      }`}
                      onClick={() => handleIndicatorClick(i)}
                      onMouseDown={(e) => e.stopPropagation()}   
                      onTouchStart={(e) => e.stopPropagation()}    
                      onPointerDown={(e) => e.stopPropagation()}
                      aria-label={`Go to slide ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* MAIN */}
      <main className="qr-main">
        <div className="qr-main-inner">
          {/* Info */}
          <div className="qr-info-col">
            <section className="qr-card">
              <h2>Overview</h2>
              <p>{overview}</p>
            </section>

            <section className="qr-card">
              <h2>How It Works</h2>
              <p>
                <strong>1. Join:</strong> Enter your email address and tap Sign
                Up — no app required.
              </p>
              <p>
                <strong>2. Get Your Link:</strong> We'll email you a personal
                member link; save it.
              </p>
              <p>
                <strong>3. Redeem Offers:</strong> On your visit, open the link,
                pick an offer, and show the code before paying.
              </p>
              <em>
                Most offers are single-use and may be limited by day/time;
                cannot be combined. We'll send occasional member emails. You can
                unsubscribe anytime.
              </em>
            </section>

            <section className="qr-card">
              <h2>Member Benefits</h2>
              <p>{benefits}</p>
            </section>
          </div>

          {/* Signup */}
          <aside className="qr-signup-col" id="signup">
            <div className="qr-signup">
              <h2>Join Our Loyalty Program</h2>
              <p className="qr-signup-sub">
                Get gentle reminders and member-only offers.
              </p>

              {successMessage && (
                <div className="qr-msg ok">{successMessage}</div>
              )}
              {errorMessage && <div className="qr-msg err">{errorMessage}</div>}

              <form onSubmit={handleSubmit} noValidate>
                <div className="qr-field">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    inputMode="email"
                    autoComplete="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <label className="qr-consent">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    required
                  />
                  <span>
                    I agree to receive promotional emails. You can
                    unsubscribe anytime.
                  </span>
                </label>

                <button
                  type="submit"
                  className="qr-cta"
                  disabled={isSubmitting || !consent || !email.trim()}
                >
                  {isSubmitting ? "Signing Up..." : "Sign Up Now"}
                </button>
              </form>
            </div>
          </aside>
        </div>
      </main>

      {/* FOOTER */}
      <footer className="qr-footer">
        <p>
          Powered by <img src={blockClubLogo} alt="Block Club" className="qr-footer-logo" />
        </p>
      </footer>
    </div>
  );
}

export default QRSignupCard;
